import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useIsDesktop } from "../components/Layout";
import ProjectTopNav from "../components/ProjectTopNav";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import {
  FileText,
  Plus,
  ArrowLeft,
  Calendar,
  Cloud,
  Sun,
  CloudRain,
  Snowflake,
  Users,
  Clock,
  Car,
  Edit,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  ListTodo,
  MoreVertical,
  ChevronRight,
  ChevronDown,
  Eye,
  Download,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AutoCreateDailyReportsModal from "../components/AutoCreateDailyReportsModal";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import MobileBautagesbericht from "./mobile/MobileBautagesbericht";

const API = '/api';

// Weather options
const WEATHER_OPTIONS = [
  { value: "sonnig", label: "Sonnig", icon: Sun },
  { value: "bewoelkt", label: "Bewölkt", icon: Cloud },
  { value: "regen", label: "Regen", icon: CloudRain },
  { value: "schnee", label: "Schnee", icon: Snowflake },
];

// ============ PDF HELPERS ============
async function fetchReportPdf(reportId) {
  const res = await authFetch(`${API}/daily-reports/${reportId}/pdf?inline=true`);
  if (!res.ok) {
    // Versuche die echte Fehlermeldung vom Backend zu lesen — hilft bei Live-Debugging
    let detail = "";
    try {
      const j = await res.json();
      detail = j?.detail || j?.message || "";
    } catch {}
    const err = new Error(detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return await res.blob();
}

async function openReportPdfPreview(report) {
  try {
    toast.loading("PDF wird erstellt...", { id: `pdf-${report.id}` });
    const blob = await fetchReportPdf(report.id);
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) {
      // Popup blocked – fall back to download
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
    }
    toast.success("PDF geöffnet", { id: `pdf-${report.id}` });
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    const detail = e?.detail || e?.message || "";
    const msg = detail
      ? `PDF-Vorschau fehlgeschlagen: ${detail}`
      : "PDF-Vorschau fehlgeschlagen. Ursache: Bildverarbeitung / Datei zu groß / Timeout.";
    toast.error(msg, {
      id: `pdf-${report.id}`,
      duration: 8000,
    });
  }
}

async function downloadReportPdf(report) {
  try {
    toast.loading("PDF wird erstellt...", { id: `pdf-dl-${report.id}` });
    const res = await authFetch(`${API}/daily-reports/${report.id}/pdf`);
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || j?.message || ""; } catch {}
      const err = new Error(detail || `HTTP ${res.status}`);
      err.status = res.status;
      err.detail = detail;
      throw err;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    // Dateiname aus Content-Disposition (sprechend vom Backend)
    let filename = `Bautagesbericht_${report.date || ""}.pdf`;
    const cd = res.headers.get("content-disposition") || "";
    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
    if (utf8Match) {
      try { filename = decodeURIComponent(utf8Match[1]); } catch { /* keep */ }
    } else if (asciiMatch) {
      filename = asciiMatch[1];
    }
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success("Download gestartet", { id: `pdf-dl-${report.id}` });
  } catch (e) {
    const detail = e?.detail || e?.message || "";
    const msg = detail
      ? `PDF konnte nicht erzeugt werden: ${detail}`
      : "PDF konnte nicht erzeugt werden. Ursache: Bildverarbeitung / Datei zu groß / Timeout.";
    toast.error(msg, { id: `pdf-dl-${report.id}`, duration: 8000 });
  }
}

// Helper: generischer Download mit Content-Disposition-Extraktion und aussagekräftigen Toasts
async function _downloadBlob(url, fallbackName, toastId, loadingMsg, successMsg, failMsg) {
  try {
    toast.loading(loadingMsg, { id: toastId });
    const res = await authFetch(url);
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || j?.message || ""; } catch {}
      const err = new Error(detail || `HTTP ${res.status}`); err.detail = detail;
      throw err;
    }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    let filename = fallbackName;
    const cd = res.headers.get("content-disposition") || "";
    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
    if (utf8Match) {
      try { filename = decodeURIComponent(utf8Match[1]); } catch { /* keep */ }
    } else if (asciiMatch) {
      filename = asciiMatch[1];
    }
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    toast.success(successMsg, { id: toastId });
  } catch (e) {
    const detail = e?.detail || e?.message || "";
    toast.error(detail ? `${failMsg}: ${detail}` : failMsg, { id: toastId, duration: 8000 });
  }
}

async function downloadPhotoDocPdf(report) {
  const photoCount = (report?.photos?.length || report?.photo_count || 0) + (report?.disruption_photos?.length || report?.disruption_photo_count || 0);
  if (photoCount === 0) {
    toast.info("Keine Fotos in diesem Bericht — Fotodokumentation nicht verfügbar.", { duration: 5000 });
    return;
  }
  await _downloadBlob(
    `${API}/daily-reports/${report.id}/photo-doc-pdf`,
    `Fotodokumentation_${report.date || ""}.pdf`,
    `photodoc-${report.id}`,
    "Fotodokumentation wird erstellt…",
    "Fotodokumentation heruntergeladen",
    "Fotodokumentation konnte nicht erzeugt werden",
  );
}

async function downloadReportZip(report) {
  await _downloadBlob(
    `${API}/daily-reports/${report.id}/zip`,
    `Bautagesbericht_${report.date || ""}.zip`,
    `zip-${report.id}`,
    "ZIP wird erstellt (Bericht + Fotodoku + Fotos)…",
    "ZIP-Komplett-Export heruntergeladen",
    "ZIP konnte nicht erzeugt werden",
  );
}

// ============ MOBILE REPORT CARD ============
function MobileReportCard({ report, onEdit, onDelete, onView }) {
  const WeatherIcon = WEATHER_OPTIONS.find(w => w.value === report.weather)?.icon || Cloud;
  const activityCount = report.activities?.length || 0;
  const linkedTaskCount = report.activities?.filter(a => a.source_task_id)?.length || 0;
  
  return (
    <div 
      className="bg-white border rounded-xl p-4 shadow-sm active:scale-[0.98] transition-transform"
      onClick={() => onView(report)}
      data-testid={`mobile-report-card-${report.id}`}
    >
      {/* Date & Weather */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              {format(parseISO(report.date), "dd.MM.yyyy")}
              <ReportStatusBadge report={report} size="xs" />
            </div>
            <div className="text-xs text-gray-500">
              {format(parseISO(report.date), "EEEE", { locale: de })}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
          <WeatherIcon className="w-4 h-4" />
          <span className="text-xs">{WEATHER_OPTIONS.find(w => w.value === report.weather)?.label}</span>
        </div>
      </div>
      
      {/* Stats Row */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1 text-gray-600">
          <ListTodo className="w-4 h-4" />
          <span>{activityCount} Tätigkeiten</span>
        </div>
        {linkedTaskCount > 0 && (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span>{linkedTaskCount} aus Aufgaben</span>
          </div>
        )}
      </div>
      
      {/* Notes Preview */}
      {report.notes && (
        <p className="text-sm text-gray-500 line-clamp-2 mt-2 border-t pt-2">
          {report.notes}
        </p>
      )}
      
      {/* Action row */}
      <div
        className="flex items-center gap-2 mt-3 pt-3 border-t"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-9"
          onClick={(e) => { e.stopPropagation(); openReportPdfPreview(report); }}
          data-testid={`m-report-pdf-preview-${report.id}`}
        >
          <Eye className="w-4 h-4 mr-1" /> PDF Vorschau
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3"
          onClick={(e) => { e.stopPropagation(); downloadReportPdf(report); }}
          data-testid={`m-report-pdf-download-${report.id}`}
          title="PDF herunterladen"
        >
          <Download className="w-4 h-4" />
        </Button>
        <div className="text-xs text-gray-400 ml-auto flex items-center">
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

// ============ REPORT CARD ============
function ReportCard({ report, onEdit, onDelete, onView }) {
  const WeatherIcon = WEATHER_OPTIONS.find(w => w.value === report.weather)?.icon || Cloud;
  const activityCount = report.activities?.length || 0;
  const linkedTaskCount = report.activities?.filter(a => a.source_task_id)?.length || 0;
  
  return (
    <div 
      className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(report)}
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              <span>{format(parseISO(report.date), "EEEE, dd. MMMM yyyy", { locale: de })}</span>
              {report.report_number && (
                <span
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100"
                  data-testid={`report-number-${report.id}`}
                >
                  {report.report_number}
                </span>
              )}
              <ReportStatusBadge report={report} size="xs" />
            </div>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <WeatherIcon className="w-3 h-3" />
              {WEATHER_OPTIONS.find(w => w.value === report.weather)?.label || "Keine Angabe"}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); openReportPdfPreview(report); }}
            data-testid={`report-pdf-preview-${report.id}`}
            title="PDF Vorschau"
          >
            <Eye className="w-4 h-4 mr-1" /> Vorschau
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); downloadReportPdf(report); }}
            data-testid={`report-pdf-download-${report.id}`}
            title="PDF herunterladen"
          >
            <Download className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-gray-100">
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); downloadPhotoDocPdf(report); }}
                data-testid={`report-photodoc-${report.id}`}>
                <FileText className="w-4 h-4 mr-2" />
                Fotodokumentation (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); downloadReportZip(report); }}
                data-testid={`report-zip-${report.id}`}>
                <Download className="w-4 h-4 mr-2" />
                Komplett-Export (ZIP)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(report); }}>
                <Edit className="w-4 h-4 mr-2" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDelete(report); }}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">{activityCount}</div>
          <div className="text-xs text-gray-500">Tätigkeiten</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">{linkedTaskCount}</div>
          <div className="text-xs text-gray-500">Aus Aufgaben</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">{report.employees_on_site?.length || 0}</div>
          <div className="text-xs text-gray-500">Mitarbeiter</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-gray-700">{report.total_work_hours?.toFixed(1) || 0}h</div>
          <div className="text-xs text-gray-500">Arbeitszeit</div>
        </div>
      </div>
      
      {/* Notes preview */}
      {report.notes && (
        <div className="mt-3 pt-3 border-t">
          <p className="text-sm text-gray-600 line-clamp-2">{report.notes}</p>
        </div>
      )}
    </div>
  );
}

// ============ ACTIVITY ROW ============
function ActivityRow({ activity, onRemove, isEditing }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group">
      {activity.source_task_id ? (
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-4 h-4 text-green-600" />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
          <ListTodo className="w-4 h-4 text-gray-600" />
        </div>
      )}
      
      <div className="flex-1">
        <div className="font-medium text-gray-900">{activity.description}</div>
        {activity.employee_name && (
          <div className="text-xs text-gray-500">{activity.employee_name}</div>
        )}
        {activity.source_task_id && (
          <Badge variant="outline" className="mt-1 text-xs">
            Aus Aufgabe
          </Badge>
        )}
      </div>
      
      <div className="text-right">
        <div className="font-semibold">{activity.hours}h</div>
      </div>
      
      {isEditing && (
        <button
          onClick={() => onRemove(activity.id)}
          className="p-1 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ============ DESKTOP SPLIT-VIEW: Sidebar mit Liste + Detail-Vorschau ============
function ReportsSplitView({ reports, projectId, isAdmin, drPerms, currentUser, onEdit, onDelete, onCreate, onDuplicate, onChangeStatus, onChangeTitle, onChanged }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(reports[0]?.id || null);

  // Default-Selektion aktualisieren wenn Reports geladen sind oder sich ändern
  useEffect(() => {
    if (!selectedId && reports.length > 0) setSelectedId(reports[0].id);
    if (selectedId && !reports.find(r => r.id === selectedId) && reports.length > 0) {
      setSelectedId(reports[0].id);
    }
  }, [reports, selectedId]);

  // Sortiert + gefiltert
  const sorted = useMemo(() => {
    return [...reports].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [reports]);

  const filtered = useMemo(() => {
    return sorted.filter(r => {
      if (monthFilter !== "all") {
        const m = (r.date || "").slice(0, 7); // YYYY-MM
        if (m !== monthFilter) return false;
      }
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      const hay = [
        r.report_number || "",
        r.date || "",
        r.notes || "",
        r.disruptions || "",
        ...(r.activities || []).map(a => `${a.source_task_title || ""} ${a.description || ""}`),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, query, monthFilter]);

  // Monatsliste für Filter
  const months = useMemo(() => {
    const set = new Set(sorted.map(r => (r.date || "").slice(0, 7)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [sorted]);

  const selected = reports.find(r => r.id === selectedId) || null;

  if (reports.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-12 text-center">
        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Berichte</h3>
        <p className="text-gray-500 mb-4">
          Erstellen Sie Ihren ersten Bautagesbericht.
        </p>
        <Button onClick={onCreate}>
          <Plus className="w-4 h-4 mr-2" />Neuer Bericht
        </Button>
      </div>
    );
  }

  const status = (r) => {
    // Bevorzugt explizit gespeicherten Status, sonst Heuristik (Legacy-Daten)
    if (r.status) {
      const s = String(r.status).toLowerCase();
      if (s === "final" || s === "finalisiert") return "final";
      return "draft";
    }
    const hasPersonnel = (r.personnel || r.employees_on_site || []).length > 0;
    const hasActivities = (r.activities || []).length > 0;
    return hasPersonnel && hasActivities ? "final" : "draft";
  };

  const role = (currentUser?.role || "").toLowerCase();
  // Granulare Rechte: aus drPerms ableiten — Fallback auf Rollen-Logik wenn (noch) nicht geladen
  const canEdit = drPerms ? !!(drPerms.edit || drPerms.create) : (role === "admin" || role === "projektleiter" || role === "geschaeftsfuehrung");
  const canDelete = drPerms ? !!drPerms.delete : (role === "admin");
  const canDuplicate = drPerms ? !!drPerms.duplicate : (role === "admin" || role === "projektleiter" || role === "geschaeftsfuehrung");
  const canPdf = drPerms ? !!drPerms.pdf : true;
  const canManage = canEdit;  // Legacy-Alias für Dropdown-Items „Bearbeiten / Titel ändern / Reset"
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const openPdf = (r) => {
    // Token aus localStorage holen und PDF in neuem Tab öffnen
    const url = `${apiBase}/projects/${projectId}/daily-reports/${r.id}/pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const promptTitle = (r) => {
    const cur = r.title || (r.activities?.[0]?.description || "").split("\n")[0] || "";
    const nt = window.prompt("Neuer Titel / Haupttätigkeit:", cur);
    if (nt !== null && nt.trim() !== cur) onChangeTitle?.(r, nt.trim());
  };

  const confirmReset = (r) => {
    if (!window.confirm("Bericht wurde bereits finalisiert. Wirklich wieder als Entwurf öffnen?")) return;
    onChangeStatus?.(r, "entwurf");
  };

  return (
    <div className="grid grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 280px)" }}>
      {/* ===== SIDEBAR ===== */}
      <aside className="col-span-12 lg:col-span-4 xl:col-span-3 bg-white border rounded-lg flex flex-col overflow-hidden">
        {/* Search + Filter */}
        <div className="p-3 border-b space-y-2">
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Bericht suchen..."
              className="h-9 pl-8"
              data-testid="btb-search-input"
            />
            <FileText className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          </div>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="h-9" data-testid="btb-month-filter">
              <SelectValue placeholder="Alle Monate" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Monate</SelectItem>
              {months.map(m => {
                try {
                  const d = parseISO(m + "-01");
                  return <SelectItem key={m} value={m}>{format(d, "MMMM yyyy", { locale: de })}</SelectItem>;
                } catch {
                  return <SelectItem key={m} value={m}>{m}</SelectItem>;
                }
              })}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1" data-testid="btb-sidebar-list">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Kein Bericht gefunden.
            </div>
          ) : (
            filtered.map(r => {
              const isActive = r.id === selectedId;
              const dayStr = r.date ? format(parseISO(r.date), "EEE, dd.MM.", { locale: de }) : "—";
              const firstAct = (r.activities || [])[0];
              const titleText =
                r.title?.trim() ||
                firstAct?.source_task_title ||
                (firstAct?.description || "").split("\n")[0] ||
                (r.notes || "").split("\n")[0] ||
                "";
              const st = status(r);
              const isFinal = st === "final";
              return (
                <div
                  key={r.id}
                  className={`group relative w-full text-left border-b border-gray-100 transition-colors ${
                    isActive ? "bg-blue-50 border-l-4 border-l-blue-600" : "hover:bg-gray-50 border-l-4 border-l-transparent"
                  }`}
                  data-testid={`btb-sidebar-item-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className="w-full text-left px-3 py-2.5 pr-9"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {r.report_number && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {r.report_number}
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">
                            {dayStr}
                          </span>
                        </div>
                        {titleText && (
                          <div className="text-xs text-gray-700 mt-0.5 truncate">{titleText}</div>
                        )}
                      </div>
                      <span
                        className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded shrink-0 ${
                          isFinal ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {isFinal ? "Final" : "Entwurf"}
                      </span>
                    </div>
                    {/* Auto-/Unbearbeitet-Badges */}
                    {(r.auto_created || r.auto_generated || r.is_unedited) && (
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        {(r.auto_created || r.auto_generated) && (
                          <span
                            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 inline-flex items-center gap-0.5"
                            data-testid={`btb-badge-auto-${r.id}`}
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            Automatisch
                          </span>
                        )}
                        {r.is_unedited && (
                          <span
                            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700"
                            data-testid={`btb-badge-unedited-${r.id}`}
                          >
                            Unbearbeitet
                          </span>
                        )}
                      </div>
                    )}
                  </button>

                  {/* 3-Punkte-Menü — immer dezent rechts sichtbar, klar antippbar */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-opacity opacity-60 group-hover:opacity-100 focus:opacity-100"
                        aria-label="Aktionen"
                        data-testid={`btb-item-menu-${r.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {canManage ? (
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); onEdit?.(r); }}
                          data-testid={`btb-item-action-open-${r.id}`}
                        >
                          Bearbeiten
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); setSelectedId(r.id); }}
                          data-testid={`btb-item-action-open-${r.id}`}
                        >
                          Öffnen
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openPdf(r); }}
                        disabled={!canPdf}
                        data-testid={`btb-item-action-pdf-${r.id}`}
                      >
                        PDF anzeigen
                      </DropdownMenuItem>
                      {canDuplicate && (
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); onDuplicate?.(r); }}
                          data-testid={`btb-item-action-duplicate-${r.id}`}
                        >
                          Duplizieren
                        </DropdownMenuItem>
                      )}
                      {canManage && (
                        <>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); promptTitle(r); }}
                            data-testid={`btb-item-action-title-${r.id}`}
                          >
                            Titel ändern
                          </DropdownMenuItem>
                          {isFinal && (
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); confirmReset(r); }}
                              data-testid={`btb-item-action-reset-${r.id}`}
                            >
                              Als Entwurf zurücksetzen
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); onDelete?.(r); }}
                            className="text-red-600 focus:text-red-700 focus:bg-red-50"
                            data-testid={`btb-item-action-delete-${r.id}`}
                          >
                            Löschen
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* ===== DETAIL PREVIEW ===== */}
      <main className="col-span-12 lg:col-span-8 xl:col-span-9 bg-white border rounded-lg">
        {selected ? (
          <ReportPreview
            report={selected}
            projectId={projectId}
            isAdmin={isAdmin}
            drPerms={drPerms}
            onEdit={onEdit}
            onDelete={onDelete}
            onChanged={onChanged}
          />
        ) : (
          <div className="p-12 text-center text-gray-400">
            Wähle einen Bericht aus der Liste.
          </div>
        )}
      </main>
    </div>
  );
}

// ============ STATUS BADGE ============
function ReportStatusBadge({ report, size = "sm" }) {
  const status = (report?.status || "entwurf").toLowerCase();
  const isAuto = !!report?.auto_created && !report?.bearbeitet && !report?.is_manually_edited;
  const cls = size === "xs" ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5";
  if (status === "final" || status === "finalisiert") {
    return (
      <span
        data-testid={`report-status-${report.id}`}
        className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 ${cls}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Final
      </span>
    );
  }
  if (status === "archiviert") {
    return (
      <span data-testid={`report-status-${report.id}`}
        className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wide rounded-full bg-zinc-200 text-zinc-700 border border-zinc-300 ${cls}`}>
        Archiviert
      </span>
    );
  }
  if (isAuto) {
    return (
      <span data-testid={`report-status-${report.id}`}
        className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wide rounded-full bg-blue-50 text-blue-700 border border-blue-200 ${cls}`}
        title="Automatisch angelegt, noch nicht bearbeitet">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Auto / Unbearbeitet
      </span>
    );
  }
  return (
    <span data-testid={`report-status-${report.id}`}
      className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wide rounded-full bg-amber-50 text-amber-700 border border-amber-200 ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Entwurf
    </span>
  );
}

// ============ REPORT PREVIEW (rechter Bereich) ============
function ReportPreview({ report, projectId, isAdmin, drPerms, onEdit, onDelete, onChanged }) {
  const navigate = useNavigate();
  const personnel = report.personnel || report.employees_on_site || [];
  const activities = report.activities || [];
  const totalHours = report.total_work_hours || 0;
  const WeatherIcon = WEATHER_OPTIONS.find(w => w.value === report.weather)?.icon || Cloud;
  const canEdit = drPerms ? !!(drPerms.edit || drPerms.create) : isAdmin;
  const canDelete = drPerms ? !!drPerms.delete : isAdmin;
  const canPdf = drPerms ? !!drPerms.pdf : true;
  const canFinalize = drPerms ? !!drPerms.finalize : isAdmin;
  const statusNorm = (report?.status || "entwurf").toLowerCase();
  const isFinal = statusNorm === "final" || statusNorm === "finalisiert";

  const setStatus = async (newStatus) => {
    const confirmMsg = newStatus === "final"
      ? "Diesen Bericht wirklich auf Final setzen? Danach ist klar dokumentiert, dass der Bericht fertiggestellt wurde."
      : "Diesen Bericht auf Entwurf zurücksetzen?";
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await authFetch(`${API}/projects/${projectId}/daily-reports/${report.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      toast.success(newStatus === "final" ? "Bericht auf Final gesetzt" : "Bericht auf Entwurf gesetzt");
      onChanged?.();
    } catch (e) {
      toast.error("Statuswechsel fehlgeschlagen: " + e.message);
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid="btb-preview">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 pb-4 border-b">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {report.report_number && (
              <span className="text-xs font-mono px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100">
                {report.report_number}
              </span>
            )}
            <ReportStatusBadge report={report} />
            <span className="text-sm text-gray-500">
              {format(parseISO(report.date), "EEEE, dd. MMMM yyyy", { locale: de })}
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-1 font-[Manrope] flex items-center gap-2">
            <WeatherIcon className="w-5 h-5 text-gray-400" />
            {report.project_name}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openReportPdfPreview(report)}
            disabled={!canPdf}
            title={!canPdf ? "Keine Berechtigung zum Erzeugen von PDFs" : undefined}
            data-testid="btb-preview-pdf-btn"
          >
            <Eye className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadReportPdf(report)}
            disabled={!canPdf}
            title={!canPdf ? "Keine Berechtigung zum Erzeugen von PDFs" : "PDF Bericht herunterladen"}
            data-testid="btb-preview-download-btn"
          >
            <Download className="w-4 h-4" />
          </Button>
          {canPdf && ((report.photos?.length || report.photo_count || 0) + (report.disruption_photos?.length || report.disruption_photo_count || 0)) > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadPhotoDocPdf(report)}
              title="Separate Fotodokumentation als PDF"
              data-testid="btb-preview-photodoc-btn"
            >
              <FileText className="w-4 h-4 mr-1" /> Fotodoku
            </Button>
          )}
          {canPdf && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadReportZip(report)}
              title="Bericht + Fotodoku + Fotos als ZIP"
              data-testid="btb-preview-zip-btn"
            >
              <Download className="w-4 h-4 mr-1" /> ZIP
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={() => onEdit(report)} data-testid="btb-preview-open-btn">
              <Edit className="w-4 h-4 mr-1" /> Bearbeiten
            </Button>
          )}
          {canFinalize && !isFinal && (
            <Button
              size="sm"
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={() => setStatus("final")}
              data-testid="btb-preview-finalize-btn"
              title="Bericht als final markieren"
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Auf Final setzen
            </Button>
          )}
          {canFinalize && isFinal && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setStatus("entwurf")}
              data-testid="btb-preview-unfinalize-btn"
              title="Bericht zurück auf Entwurf setzen"
            >
              <Edit className="w-4 h-4 mr-1" /> Auf Entwurf zurück
            </Button>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Mitarbeiter</div>
          <div className="text-xl font-bold text-gray-900">{personnel.length}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Arbeitszeit</div>
          <div className="text-xl font-bold text-blue-600">{totalHours.toFixed(2).replace(".", ",")} h</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Tätigkeiten</div>
          <div className="text-xl font-bold text-gray-900">{activities.length}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Fotos</div>
          <div className="text-xl font-bold text-gray-900">{report.photo_count ?? (report.photos || []).length}</div>
        </div>
      </div>

      {/* Personnel */}
      {personnel.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" /> Mitarbeiter
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {personnel.slice(0, 8).map((p, i) => (
              <span key={i} className="text-xs bg-blue-50 text-blue-800 px-2 py-1 rounded border border-blue-100">
                {p.name || p.user_name}
                {p.work_start && p.work_end && (
                  <span className="ml-1 text-blue-500 font-mono">{p.work_start}–{p.work_end}</span>
                )}
              </span>
            ))}
            {personnel.length > 8 && (
              <span className="text-xs text-gray-500 px-2 py-1">+{personnel.length - 8} weitere</span>
            )}
          </div>
        </section>
      )}

      {/* Activities */}
      {activities.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-blue-600" /> Tätigkeiten
          </h3>
          <div className="space-y-2">
            {activities.slice(0, 2).map((a, i) => {
              const title = a.source_task_title || (a.description || "").split("\n")[0] || "";
              // Defensive: strip leading "Title\n" repetitions from description (legacy bug)
              let body = a.description || "";
              const t = (title || "").trim();
              if (t) {
                let safety = 5;
                while (safety-- > 0) {
                  const leading = body.replace(/^[\s\r\n]+/, "");
                  if (leading === t) { body = ""; break; }
                  if (leading.startsWith(t + "\n") || leading.startsWith(t + "\r\n")) {
                    body = leading.slice(t.length).replace(/^[\r\n]+/, "");
                    continue;
                  }
                  break;
                }
              }
              if (body === title) body = "";
              return (
                <div key={a.id || i} className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold text-sm shrink-0">#{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-gray-900">{title}</div>
                      {body && <div className="text-xs text-gray-600 mt-1 line-clamp-3 whitespace-pre-line">{body}</div>}
                    </div>
                  </div>
                </div>
              );
            })}
            {activities.length > 2 && (
              <button
                type="button"
                onClick={() => onEdit(report)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                +{activities.length - 2} weitere Tätigkeiten – Bericht öffnen →
              </button>
            )}
          </div>
        </section>
      )}

      {/* Disruptions */}
      {report.disruptions && (
        <section>
          <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Besonderheiten / Störungen
          </h3>
          <div className="rounded-lg border-l-4 border-red-400 bg-red-50 p-3 text-sm text-red-900 whitespace-pre-line line-clamp-4">
            {report.disruptions}
          </div>
        </section>
      )}

      {/* Footer Meta */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-4 border-t">
        <span>Erstellt von {report.created_by_name || "—"}</span>
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(report)}
            className="text-red-500 hover:text-red-700 flex items-center gap-1"
            data-testid="btb-preview-delete-btn"
          >
            <Trash2 className="w-3 h-3" /> Löschen
          </button>
        )}
      </div>
    </div>
  );
}

// ============ MAIN COMPONENT ============
export default function DailyReports() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [duplicateDialog, setDuplicateDialog] = useState(null); // {report, targetDate, options}
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    weather: "sonnig",
    notes: "",
    activities: [],
  });
  
  // New activity
  const [newActivity, setNewActivity] = useState({
    description: "",
    hours: 0,
  });
  
  const isAdmin = user?.permissions?.can_approve_entries || user?.role === "admin";
  const canAutoCreate = ["admin", "projektleiter", "geschaeftsfuehrung"].includes((user?.role || "").toLowerCase());
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  // Granulare Bautagesbericht-Rechte (projekt-spezifisch)
  const [drPerms, setDrPerms] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const r = await authFetch(`${API}/projects/${projectId}/daily-reports/permissions`);
        if (r.ok) {
          const j = await r.json();
          setDrPerms(j.permissions || null);
        }
      } catch { /* noop */ }
    })();
  }, [projectId]);

  const reloadReports = async () => {
    try {
      const r = await authFetch(`${API}/projects/${projectId}/daily-reports`);
      if (r.ok) setReports(await r.json());
      const s = await authFetch(`${API}/projects/${projectId}/daily-reports-summary`);
      if (s.ok) setSummary(await s.json());
    } catch (e) {
      // noop
    }
  };

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;
      
      setLoading(true);
      try {
        const [projectRes, reportsRes, summaryRes] = await Promise.all([
          authFetch(`${API}/projects`),
          authFetch(`${API}/projects/${projectId}/daily-reports`),
          authFetch(`${API}/projects/${projectId}/daily-reports-summary`),
        ]);
        
        if (projectRes.ok) {
          const projects = await projectRes.json();
          const proj = projects.find(p => p.id === projectId);
          setProject(proj);
        }
        
        if (reportsRes.ok) {
          setReports(await reportsRes.json());
        }
        
        if (summaryRes.ok) {
          setSummary(await summaryRes.json());
        }
      } catch (error) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [projectId]);

  // Handlers
  const handleOpenDialog = (report = null) => {
    if (report) {
      navigate(`/app/projekte/${projectId}/berichte/${report.id}/bearbeiten`);
    } else {
      navigate(`/app/projekte/${projectId}/berichte/neu`);
    }
  };

  const handleViewDetail = (report) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      let res;
      if (selectedReport) {
        res = await authFetch(`${API}/projects/${projectId}/daily-reports/${selectedReport.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            date: formData.date,
            weather: formData.weather,
            notes: formData.notes,
            activities: formData.activities,
          }),
        });
      } else {
        res = await authFetch(`${API}/projects/${projectId}/daily-reports`, {
          method: "POST",
          body: JSON.stringify({
            project_id: projectId,
            ...formData,
          }),
        });
      }
      
      if (res.ok) {
        toast.success(selectedReport ? "Bericht aktualisiert" : "Bericht erstellt");
        setDialogOpen(false);
        // Reload
        const reportsRes = await authFetch(`${API}/projects/${projectId}/daily-reports`);
        if (reportsRes.ok) setReports(await reportsRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    
    try {
      const res = await authFetch(`${API}/projects/${projectId}/daily-reports/${selectedReport.id}`, {
        method: "DELETE",
      });
      
      if (res.ok) {
        toast.success("Bericht gelöscht");
        setDeleteDialogOpen(false);
        setDetailDialogOpen(false);
        setReports(prev => prev.filter(r => r.id !== selectedReport.id));
      }
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const addActivity = () => {
    if (!newActivity.description.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      activities: [
        ...prev.activities,
        {
          id: `temp-${Date.now()}`,
          description: newActivity.description,
          hours: parseFloat(newActivity.hours) || 0,
          source_task_id: null,
          source_task_title: null,
          employee_id: null,
          employee_name: null,
        },
      ],
    }));
    setNewActivity({ description: "", hours: 0 });
  };

  const removeActivity = (activityId) => {
    setFormData(prev => ({
      ...prev,
      activities: prev.activities.filter(a => a.id !== activityId),
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Projekt nicht gefunden</h2>
        <Button variant="outline" onClick={() => navigate("/projects")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
      </div>
    );
  }

  return (
    <div className={`${isDesktop ? 'space-y-6' : 'pb-24'}`}>
      {isDesktop && <ProjectTopNav projectId={projectId} />}
      {/* ==================== MOBILE VIEW ==================== */}
      {!isDesktop && (
        <MobileBautagesbericht
          projectId={projectId}
          projectName={project.name}
          onBack={() => navigate(`/app/projekte/${projectId}/dashboard`)}
        />
      )}

      {/* ==================== DESKTOP VIEW ==================== */}
      {isDesktop && (
        <>
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/app/projekte/${projectId}/dashboard`)}
                className="mt-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: project.color || "#6B7280" }}
                  />
                  <h1 className="text-2xl font-bold text-gray-900 font-[Manrope]">
                    Berichte
                  </h1>
                </div>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {project.name} · {reports.length} Bautagesberichte
                </p>
              </div>
            </div>
            
            {(isAdmin || drPerms?.create) && (
              <div className="flex items-center gap-2 flex-wrap">
                {canAutoCreate && (
                  <>
                    <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white cursor-pointer hover:bg-gray-50" data-testid="auto-create-toggle-wrapper">
                      <input
                        type="checkbox"
                        checked={!!project?.auto_create_daily_reports}
                        onChange={async (e) => {
                          const newVal = e.target.checked;
                          try {
                            const res = await authFetch(`${API}/projects/${projectId}`, {
                              method: "PATCH",
                              body: JSON.stringify({ auto_create_daily_reports: newVal }),
                            });
                            if (!res.ok) throw new Error();
                            setProject((p) => ({ ...p, auto_create_daily_reports: newVal }));
                            toast.success(newVal
                              ? "Automatik aktiviert. Klicke 'Bautagesberichte automatisch erstellen' um die Vorschau zu öffnen."
                              : "Automatik deaktiviert.");
                          } catch {
                            toast.error("Speichern fehlgeschlagen");
                          }
                        }}
                        className="w-4 h-4"
                        data-testid="auto-create-daily-reports-toggle"
                      />
                      <span className="text-sm font-medium">Automatisch für jeden Tag anlegen</span>
                    </label>
                    <Button
                      variant="outline"
                      onClick={() => setAutoModalOpen(true)}
                      disabled={!project?.auto_create_daily_reports}
                      title={!project?.auto_create_daily_reports
                        ? "Schalter 'Automatisch für jeden Tag anlegen' zuerst aktivieren"
                        : "Vorschau & Massenerstellung"}
                      data-testid="auto-create-reports-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
                      Bautagesberichte automatisch erstellen
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => handleOpenDialog()}
                  disabled={drPerms && !drPerms.create}
                  title={drPerms && !drPerms.create ? "Keine Berechtigung zum Erstellen von Bautagesberichten" : undefined}
                  data-testid="add-report-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Neuer Bericht
                </Button>
              </div>
            )}
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold">{summary.total_reports}</div>
                <div className="text-sm text-gray-500">Berichte</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">{summary.total_activities}</div>
                <div className="text-sm text-gray-500">Tätigkeiten</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">{summary.linked_tasks}</div>
                <div className="text-sm text-gray-500">Aus Aufgaben</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-sm text-gray-500">Letzter Bericht</div>
                <div className="font-semibold">
                  {summary.last_report_date 
                    ? format(parseISO(summary.last_report_date), "dd.MM.yyyy")
                    : "-"
                  }
                </div>
              </div>
            </div>
          )}

          {/* Reports Split Layout (Sidebar + Detail Preview) */}
          <ReportsSplitView
            reports={reports}
            projectId={projectId}
            isAdmin={isAdmin}
            drPerms={drPerms}
            currentUser={user}
            onEdit={handleOpenDialog}
            onDelete={(r) => { setSelectedReport(r); setDeleteDialogOpen(true); }}
            onDuplicate={(r) => {
              // Default-Datum: Original-Datum + 1 Tag
              let next = "";
              try {
                const d = new Date(r.date);
                d.setDate(d.getDate() + 1);
                next = d.toISOString().slice(0, 10);
              } catch { next = r.date; }
              setDuplicateDialog({
                report: r,
                targetDate: next,
                includePersonnel: true,
                includeActivities: true,
                includeDisruptions: true,
                includePhotos: false,
                includeDisruptionPhotos: false,
                includeNotes: false,
                submitting: false,
              });
            }}
            onChangeStatus={async (r, newStatus) => {
              try {
                const res = await authFetch(
                  `${API}/projects/${projectId}/daily-reports/${r.id}`,
                  { method: "PATCH", body: JSON.stringify({ status: newStatus }) }
                );
                if (!res.ok) throw new Error("Status-Änderung fehlgeschlagen");
                toast.success(newStatus === "entwurf" ? "Bericht zurück auf Entwurf gesetzt" : `Status: ${newStatus}`);
                await reloadReports();
              } catch (e) {
                toast.error(e.message);
              }
            }}
            onChangeTitle={async (r, newTitle) => {
              try {
                const res = await authFetch(
                  `${API}/projects/${projectId}/daily-reports/${r.id}`,
                  { method: "PATCH", body: JSON.stringify({ title: newTitle }) }
                );
                if (!res.ok) throw new Error("Titel-Änderung fehlgeschlagen");
                toast.success("Titel aktualisiert");
                await reloadReports();
              } catch (e) {
                toast.error(e.message);
              }
            }}
            onCreate={() => handleOpenDialog()}
            onChanged={reloadReports}
          />
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedReport ? "Bericht bearbeiten" : "Neuer Bautagesbericht"}</DialogTitle>
            <DialogDescription>
              {selectedReport 
                ? "Ändern Sie die Details des Berichts"
                : "Erstellen Sie einen neuen Bautagesbericht für dieses Projekt"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1"
                  data-testid="report-form-date"
                />
                {selectedReport && formData.date !== selectedReport.date && (
                  <div className="text-[11px] text-amber-600 mt-1" data-testid="report-form-date-changed">
                    ⚠ Datum wird geändert: {selectedReport.date} → {formData.date}
                  </div>
                )}
              </div>
              
              <div>
                <Label>Wetter</Label>
                <Select 
                  value={formData.weather} 
                  onValueChange={(v) => setFormData({ ...formData, weather: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEATHER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Tätigkeiten */}
            <div>
              <Label>Tätigkeiten</Label>
              <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                {formData.activities.map((act) => (
                  <ActivityRow
                    key={act.id}
                    activity={act}
                    onRemove={removeActivity}
                    isEditing={true}
                  />
                ))}
              </div>
              
              {/* Add activity */}
              <div className="mt-3 flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    value={newActivity.description}
                    onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                    placeholder="Tätigkeit beschreiben..."
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addActivity())}
                  />
                </div>
                <div className="w-20">
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={newActivity.hours}
                    onChange={(e) => setNewActivity({ ...newActivity, hours: e.target.value })}
                    placeholder="h"
                  />
                </div>
                <Button type="button" variant="outline" onClick={addActivity}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div>
              <Label>Besonderheiten / Störungen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Dokumentieren Sie Auffälligkeiten, Verzögerungen, Probleme..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            {selectedReport ? (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave}>
                <CheckCircle className="w-4 h-4 mr-2" />
                {selectedReport ? "Speichern" : "Erstellen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {format(parseISO(selectedReport.date), "EEEE, dd. MMMM yyyy", { locale: de })}
                </DialogTitle>
                <DialogDescription>
                  Bautagesbericht für {project?.name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Meta info */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    {(() => {
                      const WeatherIcon = WEATHER_OPTIONS.find(w => w.value === selectedReport.weather)?.icon || Cloud;
                      return <WeatherIcon className="w-5 h-5 mx-auto text-gray-600 mb-1" />;
                    })()}
                    <div className="text-sm font-medium">
                      {WEATHER_OPTIONS.find(w => w.value === selectedReport.weather)?.label || "-"}
                    </div>
                    <div className="text-xs text-gray-500">Wetter</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Users className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                    <div className="text-sm font-medium">{selectedReport.employees_on_site?.length || 0}</div>
                    <div className="text-xs text-gray-500">Mitarbeiter</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                    <div className="text-sm font-medium">{selectedReport.total_work_hours?.toFixed(1) || 0}h</div>
                    <div className="text-xs text-gray-500">Arbeitszeit</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Car className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                    <div className="text-sm font-medium">{selectedReport.total_travel_hours?.toFixed(1) || 0}h</div>
                    <div className="text-xs text-gray-500">Fahrtzeit</div>
                  </div>
                </div>
                
                {/* Employees on site */}
                {selectedReport.employees_on_site?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Mitarbeiter vor Ort
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedReport.employees_on_site.map((emp, idx) => (
                        <Badge key={idx} variant="outline">
                          {emp.user_name} ({emp.planned_hours}h)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Activities */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <ListTodo className="w-4 h-4" />
                    Tätigkeiten ({selectedReport.activities?.length || 0})
                  </h4>
                  <div className="space-y-2">
                    {selectedReport.activities?.map((act) => (
                      <ActivityRow key={act.id} activity={act} isEditing={false} />
                    ))}
                    {(!selectedReport.activities || selectedReport.activities.length === 0) && (
                      <p className="text-sm text-gray-500 italic">Keine Tätigkeiten erfasst</p>
                    )}
                  </div>
                </div>
                
                {/* Notes */}
                {selectedReport.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Besonderheiten / Störungen
                    </h4>
                    <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg">
                      {selectedReport.notes}
                    </p>
                  </div>
                )}
                
                {/* Footer */}
                <div className="text-xs text-gray-400 pt-4 border-t">
                  Erstellt von {selectedReport.created_by_name} am{" "}
                  {format(parseISO(selectedReport.created_at), "dd.MM.yyyy HH:mm")}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t flex-wrap">
                {((selectedReport.photos?.length || selectedReport.photo_count || 0) + (selectedReport.disruption_photos?.length || selectedReport.disruption_photo_count || 0)) > 0 && (
                  <div className="w-full text-xs text-gray-500 flex items-center gap-1 mb-1"
                       data-testid="detail-photo-note">
                    <FileText className="w-3.5 h-3.5" />
                    Fotos werden separat als Fotodokumentation exportiert. Der Bautagesbericht bleibt dadurch schlank und zuverlässig.
                  </div>
                )}
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Schließen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openReportPdfPreview(selectedReport)}
                  data-testid="detail-pdf-preview"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  PDF Vorschau
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadReportPdf(selectedReport)}
                  data-testid="detail-pdf-download"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Bericht
                </Button>
                {((selectedReport.photos?.length || selectedReport.photo_count || 0) + (selectedReport.disruption_photos?.length || selectedReport.disruption_photo_count || 0)) > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => downloadPhotoDocPdf(selectedReport)}
                    data-testid="detail-photodoc-download"
                    title="Separate Fotodokumentation als PDF"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Fotodoku
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => downloadReportZip(selectedReport)}
                  data-testid="detail-zip-download"
                  title="Bericht + Fotodoku + Fotos als ZIP"
                >
                  <Download className="w-4 h-4 mr-2" />
                  ZIP komplett
                </Button>
                <Button onClick={() => { setDetailDialogOpen(false); handleOpenDialog(selectedReport); }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      {/* === Duplicate Dialog === */}
      <Dialog
        open={!!duplicateDialog}
        onOpenChange={(o) => { if (!o) setDuplicateDialog(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="btb-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Bautagesbericht duplizieren</DialogTitle>
            <DialogDescription>
              {duplicateDialog?.report && (
                <>Original: <strong>{duplicateDialog.report.report_number || duplicateDialog.report.id}</strong> vom{" "}
                <strong>{duplicateDialog.report.date ? format(parseISO(duplicateDialog.report.date), "dd.MM.yyyy") : "—"}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          {duplicateDialog && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="dup-date" className="text-sm">Neues Datum</Label>
                <Input
                  id="dup-date" type="date"
                  value={duplicateDialog.targetDate}
                  onChange={(e) => setDuplicateDialog((d) => ({ ...d, targetDate: e.target.value }))}
                  data-testid="btb-duplicate-date"
                />
                {duplicateDialog.targetDate && (() => {
                  try {
                    const d = parseISO(duplicateDialog.targetDate);
                    return (
                      <p className="mt-1 text-xs text-gray-500">
                        {format(d, "EEEE", { locale: de })}
                      </p>
                    );
                  } catch { return null; }
                })()}
              </div>
              <div className="space-y-2">
                {[
                  { key: "includePersonnel", label: "Personal und Arbeitszeiten übernehmen" },
                  { key: "includeActivities", label: "Tätigkeiten übernehmen" },
                  { key: "includeDisruptions", label: "Besonderheiten / Störungen übernehmen" },
                  { key: "includePhotos", label: "Allgemeine Fotos übernehmen" },
                  { key: "includeDisruptionPhotos", label: "Fotos zu Besonderheiten / Störungen übernehmen" },
                  { key: "includeNotes", label: "Interne Notizen übernehmen" },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!duplicateDialog[opt.key]}
                      onChange={(e) => setDuplicateDialog((d) => ({ ...d, [opt.key]: e.target.checked }))}
                      className="w-4 h-4 mt-0.5"
                      data-testid={`btb-duplicate-${opt.key}`}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-gray-500 rounded-md bg-amber-50 border border-amber-200 p-2">
                Hinweis: Die Kopie wird als <strong>Entwurf</strong> angelegt. Unterschriften und finale Freigaben werden nie übernommen. Eine neue fortlaufende Berichtsnummer wird vergeben.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setDuplicateDialog(null)}
                  disabled={duplicateDialog.submitting}
                  data-testid="btb-duplicate-cancel"
                >
                  Abbrechen
                </Button>
                <Button
                  disabled={duplicateDialog.submitting || !duplicateDialog.targetDate}
                  onClick={async () => {
                    setDuplicateDialog((d) => ({ ...d, submitting: true }));
                    try {
                      const r = duplicateDialog.report;
                      const res = await authFetch(
                        `${API}/projects/${projectId}/daily-reports/${r.id}/duplicate`,
                        {
                          method: "POST",
                          body: JSON.stringify({
                            target_date: duplicateDialog.targetDate,
                            include_personnel: duplicateDialog.includePersonnel,
                            include_activities: duplicateDialog.includeActivities,
                            include_disruptions: duplicateDialog.includeDisruptions,
                            include_photos: duplicateDialog.includePhotos,
                            include_disruption_photos: duplicateDialog.includeDisruptionPhotos,
                            include_notes: duplicateDialog.includeNotes,
                          }),
                        }
                      );
                      if (!res.ok) {
                        const e = await res.json().catch(() => ({}));
                        if (res.status === 409) {
                          toast.error(e.detail || "Für dieses Datum existiert bereits ein Bautagesbericht.");
                          setDuplicateDialog((d) => ({ ...d, submitting: false }));
                          return;
                        }
                        throw new Error(e.detail || "Duplizieren fehlgeschlagen");
                      }
                      const newReport = await res.json();
                      toast.success(`Kopie erstellt: ${newReport.report_number || newReport.id} für ${format(parseISO(newReport.date), "dd.MM.yyyy")}`);
                      setDuplicateDialog(null);
                      await reloadReports();
                    } catch (e) {
                      toast.error(e.message);
                      setDuplicateDialog((d) => ({ ...d, submitting: false }));
                    }
                  }}
                  data-testid="btb-duplicate-confirm"
                >
                  {duplicateDialog.submitting ? "Wird dupliziert…" : "Duplizieren"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bautagesbericht wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedReport && (
                <span>
                  Bericht{" "}
                  <strong>{selectedReport.report_number || selectedReport.id}</strong>{" "}
                  vom{" "}
                  <strong>
                    {selectedReport.date ? format(parseISO(selectedReport.date), "dd.MM.yyyy") : "—"}
                  </strong>{" "}
                  wird gelöscht.
                </span>
              )}
              {selectedReport &&
                (selectedReport.status === "final" || selectedReport.status === "finalisiert") && (
                  <span className="block mt-3 rounded-md border border-red-300 bg-red-50 p-2.5 text-red-700">
                    <strong>Achtung:</strong> Dieser Bericht ist bereits finalisiert und kann als
                    Nachweis dienen. Löschen nur durchführen, wenn der Bericht wirklich falsch
                    angelegt wurde.
                  </span>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btb-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btb-delete-confirm"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-Create Bautagesberichte Modal */}
      <AutoCreateDailyReportsModal
        open={autoModalOpen}
        onClose={() => setAutoModalOpen(false)}
        projectId={projectId}
        onCreated={reloadReports}
      />
    </div>
  );
}
